import { BCMSCurrency } from "@bcm-dev/bcms-vite";
import { Account } from "model";

/**
 * @class QuickTransferItem
 * @classdesc Class definition for controlling the data in the quick transfer workflow
 * @param {account} from - The from account
 * @param {account} to - The to account
 * @param {STRING} option - The payment option
 * @param {bcmsCurrency} amount - The amount of the transfer
 */
export default class QuickTransferItem {
    _from;
    _to;
    _option;
    _amount;

    constructor(record) {
        this.from = record.from;
        this.to = record.to;
        this.option = record.option;
        this.amount = record.amount;
    }

    /**
     * @name from
     * @instance
     * @property set {account} - Sets the from account
     * @memberof QuickTransferItem
     */
    set from(value) {
        if(value instanceof Account) { this._from = value; }
        else { 
            this._from = value && value.id !== undefined
                ? Account.initByID(value.id) 
                : undefined; 

            if(this.to && this.to.id === value.id) {
                this.to = undefined;
            }
        }
    }
    /**
     * @name to
     * @instance
     * @property set {account} - Sets the to account
     * @memberof QuickTransferItem
     */
    set to(value) {
        if(value instanceof Account) { this._to = value; }
        else { 
            this._to = value && value.id !== undefined
                ? Account.initByID(value.id) 
                : undefined; 
        }
    }
    /**
     * @name option
     * @instance
     * @property set {STRING} - Sets the option value
     * @memberof QuickTransferItem
     */
    set option(value) { this._option = value; }
    /**
     * @name amount
     * @instance
     * @property set {bcmsCurrency} - Sets the amount value
     * @memberof QuickTransferItem
     */
    set amount(value) {
        this._amount = value instanceof BCMSCurrency ? value : BCMSCurrency.init(value);
    }

    /**
     * @name from
     * @instance
     * @property get {account} - Gets the amount value
     * @memberof QuickTransferItem
     */
    get from() { return this._from; }
    /**
     * @name to
     * @instance
     * @property get {account} - Gets the amount value
     * @memberof QuickTransferItem
     */
    get to() { return this._to; }
    /**
     * @name option
     * @instance
     * @property get {STRING} - Gets the amount value
     * @memberof QuickTransferItem
     */
    get option() { return this._option; }
    /**
     * @name amount
     * @instance
     * @property set {bcmsCurrency} - Gets the amount value
     * @memberof QuickTransferItem
     */
    get amount() { return this._amount; }

    /**
     * @name record
     * @instance
     * @description Returns an object will all properties and values
     * @memberof QuickTransferItem
     */
    record() {
        return Object.getOwnPropertyNames(this).reduce((result, key) => {
            return { ...result, [key.slice(1)] : this[key] };
        }, {});
    }

    /**
     * @name setDefaults
     * @instance
     * @description Sets the default values for anything that wasn't set
     * @memberof QuickTransferItem
     */
    setDefaults() {
        const checkingAccount = Account.getDefaultBrandedAccountByType("checking");
        const savingsAccount = Account.getDefaultBrandedAccountByType("savings");
        this.from = this.from ?? checkingAccount;
        this.to = this.to ?? savingsAccount;

        const isLoan = this.to.type === "loan";
        const otherAmount = this.option === "Other Amount";

        if((!isLoan || otherAmount) && (this.amount === undefined || this.amount.amount === undefined)) {
            this.amount = 100;
        } else if(isLoan && this.option === "Payment Due") { 
            this.amount = Account.initByType(this.to.id).lastPayment.amount;
        } 
        else if(isLoan && this.option === "Entire Balance") { 
            this.amount = Account.initByType(this.to.id).currentBalance.amount;
        }else {
            this.amount = this.amount.amount === undefined ? 100 : this.amount;
        }
    }

    /**
     * @name init
     * @static
     * @property record {OBJECT} - The object to be initalized from
     * @memberof QuickTransferItem
     */
    static init(record) {
        return new QuickTransferItem(record);
    }
}